import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { checkOverflow, getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsPickerPopup from '../ids-popup/ids-picker-popup';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsFieldHeightMixin from '../../mixins/ids-field-height-mixin/ids-field-height-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsDropdownAttributeMixin from './ids-dropdown-attributes-mixin';
import { IdsDropdownColorVariants } from './ids-dropdown-common';
import '../ids-button/ids-button';

import type IdsDropdown from './ids-dropdown';
import type IdsMultiselect from '../ids-multiselect/ids-multiselect';
import type IdsListBox from '../ids-list-box/ids-list-box';
import type IdsListBoxOption from '../ids-list-box/ids-list-box-option';
import type IdsTooltip from '../ids-tooltip/ids-tooltip';

import styles from './ids-dropdown-list.scss';
import debounce from '../../utils/ids-debounce-utils/ids-debounce-utils';
import IdsGlobal from '../ids-global/ids-global';
import { getSpecs } from '../../utils/ids-device-env-specs-utils/ids-device-env-specs-utils';
import { POPUP_EDGE_OFFSET } from '../ids-popup/ids-popup-attributes';

const Base = IdsDropdownAttributeMixin(
  IdsColorVariantMixin(
    IdsLocaleMixin(
      IdsFieldHeightMixin(
        IdsKeyboardMixin(
          IdsPickerPopup
        )
      )
    )
  )
);

/**
 * IDS Dropdown List Component
 * @type {IdsDropdownList}
 * @inherits IdsPickerPopup
 * @mixes IdsLocaleMixin
 * @part dropdown-list - the dropdown list element
 */
@customElement('ids-dropdown-list')
@scss(styles)
export default class IdsDropdownList extends Base {
  private static readonly MOBILE_VIEWPORT_PADDING = 60;

  isMultiSelect?: boolean;

  listBox?: IdsListBox | null;

  lastHovered: IdsListBoxOption | null = null;

  originalTooltips: Map<string, string> = new Map();

  /**
   * Handles auto-sizing logic for dropdown popup
   * @param {DOMRect} rect The bounding rectangle to use for sizing
   * @param {boolean} inputAutoSize Whether input auto-sizing is enabled
   * @private
   */
  private handleAutoSizing(rect: DOMRect, inputAutoSize: boolean): void {
    if (inputAutoSize) {
      this.popup?.style.setProperty('max-width', `${rect.width}px`);
      this.popup?.style.setProperty('width', `${rect.width}px`);
      if (this.localeAPI.isRTL()) this.popup?.style.setProperty('left', `${rect.left}px`);
      return;
    }

    this.popup?.style.setProperty('max-width', 'calc(100% - 40px)');
    this.popup?.style.removeProperty('width');
    this.popup?.style.removeProperty('min-width');
    this.listBox?.style.removeProperty('width');
    let maxWidth = 0;
    const options = this.options.length > 0 ? this.options : this.dropdownEl?.options || [];
    options.forEach((option) => {
      const optionWidth = option.getBoundingClientRect().width;
      if (optionWidth > maxWidth) maxWidth = optionWidth;
    });

    const inputRect = this.dropdownEl?.getInputFieldContainer()?.getBoundingClientRect();
    const startingPoint = this.localeAPI.isRTL() ? (inputRect?.right || 0) : (inputRect?.left || 0);

    // Determine the containing boundary - use splitter pane if available, else viewport
    const splitterPane = getClosest(this.popup, 'ids-splitter-pane');
    const containerElem = splitterPane?.shadowRoot?.querySelector('.ids-splitter-pane') as HTMLElement;
    const containerRect = containerElem?.getBoundingClientRect();

    let availableWidth: number;
    if (containerRect) {
      availableWidth = this.localeAPI.isRTL()
        ? startingPoint - containerRect.left
        : containerRect.right - startingPoint;
    } else {
      availableWidth = this.localeAPI.isRTL()
        ? startingPoint
        : (window.visualViewport?.width || 0) - startingPoint;
    }

    const scrollbarWidth = this.listBox ? (this.listBox.offsetWidth - this.listBox.clientWidth) : 0;
    const containerWidth = this.dropdownEl?.getInputFieldContainer()?.clientWidth || 0;
    const totalWidth = Math.max(Math.min(availableWidth, maxWidth) + scrollbarWidth, containerWidth);

    const maxAllowedWidth = containerRect?.width || Number(window?.visualViewport?.width || 0);
    if (maxAllowedWidth >= totalWidth) {
      this.popup?.style.setProperty('min-width', `${totalWidth}px`);
      this.listBox?.style.setProperty('width', `${totalWidth}px`);
    }

    if (!inputRect || !this.autoSize) {
      return;
    }

    const popupOffsetWidth = this.popup?.offsetWidth || 0;
    const rightBoundary = containerRect?.right || Number(window?.visualViewport?.width || 0);
    const leftBoundary = containerRect?.left || 0;

    const willOverflowX = this.localeAPI.isRTL()
      ? (inputRect.left || 0) - Number(popupOffsetWidth) <= leftBoundary
      : (inputRect.left || 0) + Number(popupOffsetWidth) >= rightBoundary;

    if (!this.popup) return;

    if (!willOverflowX) {
      const newX = this.localeAPI.isRTL() ? (inputRect.right - popupOffsetWidth) : inputRect.left;
      this.popup.style.setProperty('left', `${newX}px`);
      return;
    }

    const newWidth = availableWidth - 4;
    if (this.localeAPI.isRTL()) {
      const expectedWidth = inputRect.left + popupOffsetWidth;
      const newX = inputRect.left;

      if (expectedWidth < rightBoundary) {
        this.popup.style.setProperty('left', `${newX}px`);
        return;
      }

      const leftSpace = inputRect.left || 0;
      const rightSpace = (window.visualViewport?.width || 0) - (inputRect.right || 0);
      if (leftSpace < rightSpace) {
        const oppositeSpace = (window.visualViewport?.width || 0) - inputRect.left;
        const newRtlWidth = oppositeSpace - POPUP_EDGE_OFFSET;
        this.popup.style.setProperty('width', `${newRtlWidth}px`);
        this.popup?.style.setProperty('min-width', `${newRtlWidth}px`);
        return;
      }

      const toAssignLeft = startingPoint - newWidth;
      this.popup.style.setProperty('width', `${newWidth}px`);
      this.popup?.style.setProperty('min-width', `${newWidth}px`);
      this.popup?.style.setProperty('left', `${toAssignLeft}px`);
      return;
    }

    // LTR: Calculate space from input's left edge to container's right boundary
    const inputLeftEdge = inputRect.left || 0;
    const spaceFromInputToRightEdge = containerRect
      ? (rightBoundary - inputLeftEdge)
      : ((window.visualViewport?.width || 0) - inputLeftEdge);

    // If popup fits in available space, align to input's left
    // Otherwise align to right edge of container
    if (popupOffsetWidth <= spaceFromInputToRightEdge) {
      this.popup.style.setProperty('left', `${inputLeftEdge}px`);
      return;
    }

    // // Not enough room - align to right edge of container boundary
    const alignToRightEdge = rightBoundary - popupOffsetWidth;
    if (containerRect && alignToRightEdge >= leftBoundary) {
      // There's room to show full popup aligned to right edge
      this.popup.style.setProperty('left', `${alignToRightEdge}px`);
      return;
    }

    const leftSpace = inputRect.left || 0;
    const rightSpace = (window.visualViewport?.width || 0) - (inputRect.right || 0);
    if (leftSpace > rightSpace) {
      const constrainedWidth = Math.min(inputRect.right - POPUP_EDGE_OFFSET, totalWidth);
      this.listBox?.style.setProperty('width', `${constrainedWidth}px`);
      this.popup?.style.setProperty('min-width', `${constrainedWidth}px`);
      this.popup.style.setProperty('max-width', `${inputRect.right - POPUP_EDGE_OFFSET}px`);

      this.popup.style.setProperty('left', `${(inputRect.right || 0) - (this.popup?.offsetWidth || 0)}px`);
      return;
    }

    // Popup is wider than the entire container - resize it
    this.popup.style.setProperty('width', `${newWidth}px`);
    this.popup.style.setProperty('min-width', `${newWidth}px`);
    if (containerElem) this.popup.style.setProperty('left', `${leftBoundary}px`);
  }

  // For position style fixed dropdowns, live refresh dropdown dimensions
  dropdownResizeObserver: ResizeObserver = new ResizeObserver(debounce((entries: ResizeObserverEntry[]) => {
    if (this.autoSize) {
      const target = entries[0].target;
      const inputAutoSize = this.dropdownEl?.inputAutoSize !== false;

      let rect: DOMRect | undefined;
      let clientWidth: number = 0;

      if (target.tagName === 'IDS-DROPDOWN-LIST') {
        rect = this.dropdownEl?.getInputFieldContainer?.()?.getBoundingClientRect?.();
        clientWidth = this.dropdownEl?.getInputFieldContainer?.()?.clientWidth || 0;
      } else if (target.className.includes('field-container')) {
        rect = target.getBoundingClientRect?.();
        clientWidth = target?.clientWidth || 0;
      }

      if (rect) {
        rect.width = clientWidth > 0 ? clientWidth : rect.width;
        this.handleAutoSizing(rect, inputAutoSize);
      }
      return;
    }

    const rect = entries[0].contentRect;
    const fieldContainer = this.dropdownEl?.getInputFieldContainer();
    const fieldRec = fieldContainer?.getBoundingClientRect();

    if (fieldRec && fieldRec.width < rect.width) {
      this.popup?.style.setProperty('width', `${fieldRec.width}px`);
    } else {
      this.popup?.style.setProperty('width', `${rect.width}px`);
    }
  }, 30));

  constructor() {
    super();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.POSITION_STYLE
    ];
  }

  /**
   * List of available color variants for this component
   * @returns {Array<string>}
   */
  colorVariants: Array<string> = IdsDropdownColorVariants;

  template() {
    const autoSize = this.autoSize ? `auto-size="${this.autoSize}"` : '';
    const isMultiselect = this.dropdownEl?.nodeName === 'IDS-MULTISELECT';
    const showButtons = isMultiselect && this.dropdownEl
      && (this.dropdownEl.showApply || this.dropdownEl.showCancel || this.dropdownEl.showClear);
    return `<ids-popup class="ids-dropdown-list" ${autoSize} type="dropdown" part="dropdown-list" y="-1">
      <slot slot="content"></slot>
      ${showButtons ? this.generateMultiselectFooter() : ''}
    </ids-popup>`;
  }

  private get isMultiselectDatagridEditor() {
    const datagridEditorClass = 'datagrid-editor';
    return this.isMultiSelect && this.dropdownEl.classList.contains(datagridEditorClass);
  }

  /**
   * Generate the multiselect footer with buttons
   * @private
   * @returns {string} HTML string for the multiselect footer
   */
  private generateMultiselectFooter(): string {
    const btnCancel = this.dropdownEl?.showCancel ? '<ids-button appearance="secondary" class="multiselect-btn multiselect-btn-cancel" part="btn-cancel" tabindex="0"><span>Cancel</span></ids-button>' : '';
    const btnClear = this.dropdownEl?.showClear ? '<ids-button appearance="secondary" class="multiselect-btn multiselect-btn-clear" part="btn-clear" tabindex="0"><span>Clear</span></ids-button>' : '';
    const btnApply = this.dropdownEl?.showApply ? '<ids-button appearance="primary" class="multiselect-btn multiselect-btn-apply" part="btn-apply" tabindex="0"><span>Apply</span></ids-button>' : '';

    return `<div slot="content" class="multiselect-footer" part="footer">
      ${btnCancel}
      ${btnClear}
      ${btnApply}
    </div>`;
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();
    this.configureListBox();
    this.attachEventHandlers();
    this.#setPreselectedLabel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.listBox = null;
    this.lastHovered = null;
    this.dropdownResizeObserver.disconnect();
  }

  get dropdownEl(): IdsDropdown | IdsMultiselect {
    if (this.target?.tagName === 'IDS-DROPDOWN') {
      return this.target as IdsDropdown;
    }

    if (this.target?.tagName === 'IDS-MULTISELECT') {
      return this.target as IdsMultiselect;
    }

    // Fallback to host element
    const host = (this.parentElement?.parentNode as ShadowRoot)?.host;
    if (host?.tagName === 'IDS-MULTISELECT') {
      return host as IdsMultiselect;
    }
    return host as IdsDropdown;
  }

  get localeAPI() {
    return IdsGlobal.getLocale();
  }

  onHide() {
    if (this.listBox) this.listBox.style.height = '';
    this.setAriaOnMenuClose();
    this.lastHovered = null;
    this.dropdownResizeObserver.disconnect();
  }

  onShow() {
    const isDataGridFilter = this.triggerElem?.classList?.contains?.('data-grid-filter');
    if (!isDataGridFilter) {
      this.scrollIntoView({ block: 'nearest' });
    }

    this.configureListBox();
    this.configurePopup();
    this.setAriaOnMenuOpen();

    if (this.value || typeof this.value === 'string') this.selectOption(this.value);

    if (this.positionStyle === 'fixed') {
      if (!this.autoSize) {
        const fieldContainer = this.dropdownEl?.getInputFieldContainer();
        const fieldWidth = fieldContainer?.getBoundingClientRect()?.width || this.clientWidth;
        this.popup?.style.setProperty('width', `${fieldWidth}px`);
        this.popup?.style.setProperty('max-width', `${fieldWidth}px`);
      }
      this.dropdownResizeObserver.observe(this);
      const fieldContainer = this.dropdownEl?.getInputFieldContainer();
      if (fieldContainer) this.dropdownResizeObserver.observe(fieldContainer);
    } else {
      this.popup?.style.removeProperty('width');
    }
  }

  onTargetChange() {
    const id = this.getAttribute(attributes.ID);
    if (id) this.target?.setAttribute(attributes.LIST, `#${id}`);
  }

  #setPreselectedLabel() {
    if (!this.value) return;

    const selectedOptionText = this.getOption(this.value)?.textContent;
    const dropdownEl = this.dropdownEl;
    const dropdownInput = dropdownEl?.input?.input;

    if (selectedOptionText && dropdownEl && dropdownInput) {
      dropdownInput.value = selectedOptionText;
    }
  }

  private attachEventHandlers() {
    // Store original tooltip data
    this.storeOriginalTooltips();

    // Handle Tab/Shift+Tab to close dropdown and move focus
    this.listen(['Tab'], this, (e: KeyboardEvent) => {
      if (!this.popup?.visible) return;

      e.preventDefault();
      e.stopPropagation();

      const selectedOption = this.selected;
      const isDropdown = this.dropdownEl?.nodeName === 'IDS-DROPDOWN';
      const valueChanged = selectedOption && selectedOption.getAttribute(attributes.VALUE) !== this.value;

      if (isDropdown && valueChanged) {
        const dropdown = this.dropdownEl as IdsDropdown;
        const selected = this.selected;
        dropdown.selectedOptionData = {
          id: selected?.getAttribute('id') || null,
          label: selected?.textContent?.trim() || null
        };
        dropdown.value = selected.value;
      }

      if (this.dropdownEl && typeof this.dropdownEl.close === 'function') {
        this.dropdownEl.close(true);
      }
      this.moveFocus(!e.shiftKey);
    });

    this.offEvent('click.dropdown-list-box');
    this.onEvent('click.dropdown-list-box', this, (e: any) => {
      let target: HTMLElement | null = (e.target as HTMLElement);

      if (target && target.nodeName !== 'IDS-LIST-BOX-OPTION') {
        target = target.closest('ids-list-box-option');
      }

      if (target) {
        // Excluding group labels, description options, and no-results options
        if (target.hasAttribute(attributes.GROUP_LABEL)
          || target.hasAttribute(attributes.DESCRIPTION)
          || target.hasAttribute(attributes.NO_RESULTS)) return;

        const selectedValue = (target as IdsListBoxOption).value || '';
        this.value = selectedValue;

        if (this.dropdownEl?.nodeName === 'IDS-DROPDOWN') {
          const dropdown = this.dropdownEl as IdsDropdown;
          dropdown.selectedOptionData = {
            id: target.getAttribute('id'),
            label: target.textContent?.trim() || null
          };
          dropdown.value = selectedValue;
        }

        this.triggerSelectedEvent(target as IdsListBoxOption);
        if (!this.isMultiSelect && this.popup?.visible && this.dropdownEl?.nodeName !== 'IDS-MULTISELECT') {
          const isInsidePopup = !!this.dropdownEl?.closest('ids-popup');
          if (!isInsidePopup) {
            if (this.dropdownEl && typeof this.dropdownEl.close === 'function') {
              this.dropdownEl.close();
            }
          }
        }
      }
    });

    // Listen for restore column width event from data grid
    this.offEvent('restore-column-width');
    this.onEvent('restore-column-width', this.dropdownEl, () => {
      this.restoreOriginalColumnWidth();
    });

    // Handle footer button clicks for multiselect (using event delegation)
    this.offEvent('click.multiselect-footer');
    this.onEvent('click.multiselect-footer', this.container, (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      if (target.closest('.multiselect-btn-apply')) {
        e.stopPropagation();
        this.handleApplyEvent();
        return;
      }

      if (target.closest('.multiselect-btn-clear')) {
        e.stopPropagation();
        this.handleClearEvent();
        return;
      }

      if (target.closest('.multiselect-btn-cancel')) {
        e.stopPropagation();
        this.handleCancelEvent();
      }
    });

    // Handle keyboard events on footer buttons
    this.offEvent('keydown.multiselect-footer');
    this.onEvent('keydown.multiselect-footer', this.container, (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;

      const target = e.target as HTMLElement;
      if (!target?.classList.contains('multiselect-btn')) return;

      e.preventDefault();
      e.stopPropagation();
      target.click();
    });

    if (this.dropdownEl?.tooltip) {
      this.offEvent('mouseover.dropdown-list-box');
      this.onEvent('mouseover.dropdown-list-box', this.listBox, (e: any) => {
        // Restore tooltips in case they were lost during filtering
        this.restoreTooltips();

        let target: HTMLElement | null = (e.target as HTMLElement);

        if (target && target.nodeName !== 'IDS-LIST-BOX-OPTION') {
          target = target.closest('ids-list-box-option');
        }

        if (target) {
          this.lastHovered = (target as IdsListBoxOption);
          this.dropdownEl?.selectTooltip?.(this.lastHovered);
        }
      });
    }
  }

  /**
   * Handle apply button click
   */
  private handleApplyEvent(): void {
    if (this.dropdownEl && typeof this.dropdownEl.close === 'function') {
      this.dropdownEl.close();
    }
    this.triggerEvent('apply', this.dropdownEl, {
      bubbles: true,
      detail: {
        elem: this.dropdownEl,
        value: (this.dropdownEl as IdsMultiselect).value
      }
    });
  }

  /**
   * Handle clear button click
   */
  private handleClearEvent(): void {
    const multiselect = this.dropdownEl as IdsMultiselect;
    multiselect.value = [];
    this.listBox?.querySelectorAll('ids-list-box-option.is-selected').forEach((option) => {
      option.classList.remove('is-selected');
      option.removeAttribute(htmlAttributes.ARIA_SELECTED);
    });
    this.triggerEvent('clear', this.dropdownEl, {
      bubbles: true,
      detail: {
        elem: this.dropdownEl
      }
    });
  }

  /**
   * Handle cancel button click
   */
  private handleCancelEvent(): void {
    // Restore initial state before closing
    if ('restoreInitialState' in this.dropdownEl && typeof this.dropdownEl.restoreInitialState === 'function') {
      this.dropdownEl.restoreInitialState();
    }
    if (this.dropdownEl && typeof this.dropdownEl.close === 'function') {
      this.dropdownEl.close();
    }
    this.triggerEvent('cancel', this.dropdownEl, {
      bubbles: true,
      detail: {
        elem: this.dropdownEl
      }
    });
  }

  /**
   * Restores the original column width when dropdown closes
   */
  private restoreOriginalColumnWidth(): void {
    if (this.dropdownEl?.tagName === 'IDS-DATA-GRID' && this.originalColumnWidth && this.resizedColumnId) {
      if ('setColumnWidth' in this.dropdownEl && typeof this.dropdownEl.setColumnWidth === 'function') {
        this.dropdownEl.setColumnWidth(this.resizedColumnId, this.originalColumnWidth);
      }
      this.originalColumnWidth = null;
      this.resizedColumnId = null;
    }
  }

  /**
   * Triggers a `selected` event that propagates to the target element (usually an IdsDropdown)
   * @param {IdsListBoxOption | CustomEvent} [clickedElem] the actual clicked element or custom event
   * @returns {void}
   */
  private triggerSelectedEvent(clickedElem?: IdsListBoxOption | CustomEvent): void {
    let args: any;
    if (clickedElem instanceof CustomEvent) {
      args = clickedElem;
    } else {
      const selectedElem = clickedElem || this.selected;
      args = {
        bubbles: true,
        detail: {
          elem: this,
          label: selectedElem?.textContent,
          selectedElem,
          value: this.value,
        }
      };
    }

    if (this.target) {
      const event = new CustomEvent('selected', args);
      this.target.dispatchEvent(event);
    }
  }

  private initialY: number | null = null; // Store the initial Y position

  private originalColumnWidth: number | null = null; // Store original column width

  private resizedColumnId: string | null = null; // Store resized column ID

  configurePopup() {
    // External dropdown lists configured for "full" size need extra help
    // determining what size matches their target element.
    if (this.size === 'full' && this.target && !this.parentElement?.classList.contains('ids-dropdown')) {
      const targetWidth = `${Math.round((this.target as HTMLElement).getBoundingClientRect().width * 10) / 10}px`;

      let autoSize = false;
      let inputAutoSize = true;
      let activeCell: IdsDropdown | null = null;

      if (this.dropdownEl?.tagName === 'IDS-DATA-GRID') {
        activeCell = this.dropdownEl.shadowRoot!.querySelector('#ids-dropdown-active-cell') as IdsDropdown;
        autoSize = activeCell?.autoSize || false;
        inputAutoSize = activeCell?.inputAutoSize !== false;
      } else {
        autoSize = this.dropdownEl?.autoSize || false;
        inputAutoSize = this.dropdownEl?.inputAutoSize !== false;
      }

      const deviceSpec = getSpecs();
      const viewportWidth = window.visualViewport?.width || window.innerWidth;
      const maxMobileWidth = viewportWidth - IdsDropdownList.MOBILE_VIEWPORT_PADDING;

      let finalWidth = targetWidth;
      if (autoSize) {
        // Find largest option width
        this.classList.add('input-auto-size-false');
        let maxWidth = 0;
        this.options.forEach((option) => {
          const optionWidth = option.getBoundingClientRect().width;
          const optionMargin = (window.getComputedStyle(option).marginInlineEnd as any);
          if (optionWidth > maxWidth) maxWidth = optionWidth + parseFloat(optionMargin);
        });
        if (maxWidth > 0) {
          if (deviceSpec.isMobile) {
            finalWidth = `${maxMobileWidth}px`;
            this.listBox?.classList.add('mobile-auto-size');
            this.popup?.classList.add('mobile-centered');
          } else {
            finalWidth = `${maxWidth}px`;
          }
        }
      } else {
        this.listBox!.style.overflow = 'auto';
        this.listBox?.classList.remove('mobile-auto-size');
        this.popup?.classList.remove('mobile-centered');
        this.classList.remove('input-auto-size-false');
      }

      this.style.width = targetWidth;
      if (this.popup) {
        if (autoSize || !inputAutoSize) {
          this.popup.style.width = finalWidth;
          this.popup.style.maxWidth = finalWidth;

          if (this.dropdownEl?.tagName === 'IDS-DATA-GRID' && inputAutoSize) {
            const popupWidth = parseInt(finalWidth);
            const targetElement = activeCell!.parentElement;
            if (targetElement && popupWidth > targetElement.getBoundingClientRect().width) {
              const columnId = 'column' in targetElement && targetElement.column && typeof targetElement.column === 'object' && targetElement.column !== null && 'id' in targetElement.column ? targetElement.column.id : null;
              if (columnId) {
                // Store original width before resizing
                this.originalColumnWidth = targetElement.getBoundingClientRect().width;
                this.resizedColumnId = columnId as string;
                if ('setColumnWidth' in this.dropdownEl && typeof this.dropdownEl.setColumnWidth === 'function') {
                  this.dropdownEl.setColumnWidth(columnId, popupWidth);
                }
              }
            } else if (activeCell && targetElement && popupWidth < targetElement.getBoundingClientRect().width) {
              activeCell.setListboxOptionWidthToFieldContainer();
              this.popup.style.width = `${targetElement.getBoundingClientRect().width}px`;
              this.popup.style.maxWidth = `${targetElement.getBoundingClientRect().width}px`;
            }
          }
        } else {
          this.popup.style.width = targetWidth;
          this.popup.style.maxWidth = targetWidth;
        }
      }
    }

    if (this.popup) {
      if (!this.target) {
        this.popup.alignTarget = this;
      }
      this.popup.type = 'dropdown';
      this.popup.container?.classList.add('dropdown');
      this.popup.align = this.localeAPI.isRTL() ? 'bottom, right' : 'bottom, left';
      this.popup.arrow = 'none';

      const closestDataGrid = getClosest(this, 'ids-data-grid');
      // Fix aria if the menu is closed
      if (!this.popup.visible) {
        this.popup.y = -1;
        this.popup.x = 0;
        this.setAriaOnMenuClose();
        if (this.positionStyle === 'absolute') {
          this.popup.onPlace = (popupRect: DOMRect) => popupRect;
        }
      } else if (this.popup.visible && this.positionStyle === 'fixed') {
        this.popup.setAttribute(attributes.POSITION_STYLE, 'fixed');

        if (closestDataGrid && !this.getAttribute(attributes.TRIGGER_ELEM)) {
          return;
        }

        this.configurePopupPlacement();
      } else if (this.popup.visible) {
        this.popup.setPosition(0, -1, false, true);
      }
    }
  }

  /**
   * Configures the popup placement of dropdown.
   */
  configurePopupPlacement() {
    if (this.popup) {
      const input = (this.parentNode?.querySelector('ids-trigger-field')?.shadowRoot?.querySelector('.field-container') as HTMLDivElement);
      let y = (input?.getBoundingClientRect().bottom || 0) - 1;
      let x = (input?.getBoundingClientRect().x || 0);
      const root = (this.getRootNode() as any).host;

      // Store the initial y position only once
      if (this.initialY === null) {
        this.initialY = y;
      }
      this.popup.onPlace = (popupRect: DOMRect, domElement: HTMLElement) => {
        x = (input?.getBoundingClientRect().x || 0);
        const inputRect = input?.getBoundingClientRect();

        // Sync popup width with field container on every placement (including resize)
        if (!this.autoSize && !root.tagName.toLowerCase().includes('multiselect') && inputRect?.width) {
          domElement.style.setProperty('width', `${inputRect.width}px`);
          domElement.style.setProperty('max-width', `${inputRect.width}px`);
        }

        if (root.tagName.toLowerCase().includes('multiselect')) {
          const fieldContainer = root.getInputFieldContainer();
          const inputAutoSize = this.dropdownEl?.inputAutoSize !== false;

          popupRect.x = fieldContainer?.getBoundingClientRect().x || popupRect.x;
          if (this.popup && inputAutoSize) {
            this.popup.style.width = `${fieldContainer?.getBoundingClientRect().width}px`;
          } else if (this.popup && !inputAutoSize) {
            this.popup.style.setProperty('max-width', '95vw');
            this.popup.style.setProperty('width', 'auto');
          }
        }

        const willOverflowBottom = () => (inputRect.bottom || 0) + Number(this.popup?.offsetHeight)
          > Number(window?.visualViewport?.height || 0);
        const willOverflowX = () => (this.localeAPI.isRTL() ? (inputRect.right || 0) - Number(this.popup?.offsetWidth) < 0
          : (inputRect.left || 0) + Number(this.popup?.offsetWidth) >= Number(window?.visualViewport?.width || 0));

        const mobileKeyboardHeightOffset = ((document.body?.scrollHeight || 0)
          - (window?.visualViewport?.height || 0));
        const modalParent = getClosest(this.popup, 'ids-modal, ids-action-panel');
        const willOverFlowTop = () => ((inputRect.top || 0) - Number(this.popup?.offsetHeight)) < 0;

        if (modalParent) {
          (popupRect.x as string | number) = 'unset';
          (popupRect.y as string | number) = 'unset';
          let marginTop = 0;
          if (!this.hasAttribute(attributes.NO_MARGINS)) marginTop -= 17;
          if (input?.classList?.contains('error')) marginTop -= 24;

          if (marginTop !== 0) {
            domElement.style.marginTop = `${marginTop}px`;
          }

          const modalContent = modalParent.container?.querySelector('.ids-modal-content');
          let scrollableContainer: HTMLElement | null = this.dropdownEl;
          let depth = 0;
          const maxDepth = 50; // Prevent infinite loops
          while (scrollableContainer && scrollableContainer !== modalContent && depth < maxDepth) {
            const computedStyle = window.getComputedStyle(scrollableContainer);
            if (['auto', 'scroll'].includes(computedStyle.overflow) || ['auto', 'scroll'].includes(computedStyle.overflowY)) break;
            scrollableContainer = scrollableContainer.parentElement;
            depth++;
          }
          const scrollTop = (scrollableContainer ?? modalContent)?.scrollTop || 0;

          if (scrollTop && scrollTop > 0) domElement.style.marginTop = `-${17 + scrollTop}px`;
          if (willOverflowBottom() && !willOverFlowTop()) {
            const popupYCoords = inputRect.top - (this.popup?.offsetHeight || 0);
            const modalGapToBody = (window?.visualViewport?.height || 0) - modalParent.container.querySelector('.ids-modal-container').getBoundingClientRect().height;
            domElement.style.marginTop = '0px';
            popupRect.y = popupYCoords - (modalGapToBody / 2) + (mobileKeyboardHeightOffset / 2) + 1;
            this.popup?.container?.classList.add('flipped');
          } else if (willOverflowBottom()) {
            const availableSpace = Number(window?.visualViewport?.height || 0)
              - (inputRect.bottom || 0);
            this.popup!.maxHeight = availableSpace;
          }
          return popupRect;
        }

        const deviceSpec = getSpecs();
        const mobileKeyboardOffsetTop = window?.visualViewport?.offsetTop || 0;
        y = (inputRect.bottom + mobileKeyboardOffsetTop);
        popupRect.y = y + (this.compact || this.fieldHeight === 'xs' ? 1 : 0);

        const xOffset = (this.isMultiselectDatagridEditor ? 1 : 0);
        if (this.localeAPI.isRTL()) {
          popupRect.x = (inputRect.right || 0) - (this.popup?.offsetWidth || 0) - xOffset;
        } else {
          popupRect.x = x + xOffset;
        }
        const overflowBottom = willOverflowBottom();
        const overflowTop = willOverFlowTop();
        if (this.listBox) this.listBox.style.height = '';
        const spaceOnBottom = Number(window?.visualViewport?.height || 0) - (inputRect.bottom || 0);
        const spaceOnTop = (inputRect.top || 0);
        this.popup?.container?.classList.remove('flipped');

        if (!deviceSpec.isMobile) {
          // Flip it up
          if (overflowBottom && !overflowTop) {
            popupRect.y = (spaceOnTop + (mobileKeyboardOffsetTop || 0)) - Number(this.popup?.offsetHeight);
            this.popup?.container?.classList.add('flipped');
            if (this.listBox) this.listBox.style.height = '';
          } else if (!overflowBottom && overflowTop) {
            const newY = inputRect.bottom + (mobileKeyboardOffsetTop || 0);
            popupRect.y = newY;
          } else if (overflowBottom && overflowTop) {
            if (spaceOnTop > spaceOnBottom) {
              const newHeight = (window?.visualViewport?.height || 0) - inputRect.top;
              this.popup?.container?.classList.add('flipped');
              if (this.listBox) this.listBox.style.height = `${newHeight}px`;
              popupRect.y = (spaceOnTop + (mobileKeyboardOffsetTop || 0)) - newHeight;
              this.popup?.container?.classList.add('flipped');
            } else {
              const newHeight = spaceOnBottom - 10;
              const newY = inputRect.bottom + (mobileKeyboardOffsetTop || 0);
              if (this.listBox) this.listBox.style.height = `${newHeight}px`;
              popupRect.y = newY;
            }
          }
        } else {
          // Calculate y coordinates for mobile
          const visualViewport = window.visualViewport;
          const mobileSpaceOnTop = inputRect.top - (visualViewport?.pageTop || 0);
          const mobileSpaceOnBottom = (visualViewport?.height || 0) - (inputRect.bottom - (visualViewport?.offsetTop || 0));

          let newY = 0;
          if (mobileSpaceOnTop > mobileSpaceOnBottom) {
            newY = (inputRect.top - popupRect.height) + 1;
            this.popup?.container?.classList.add('flipped');
          } else {
            newY = inputRect.bottom - 1;
          }

          if (newY > 0) {
            popupRect.y = newY;
          }
        }

        if (willOverflowX()) {
          const popupOffsetWidth = (this.popup?.offsetWidth || 0);
          if (this.localeAPI.isRTL()) {
            const expectedWidth = inputRect.left + popupOffsetWidth;
            const newX = inputRect.left;

            if (expectedWidth < (visualViewport?.width || 0)) {
              popupRect.x = newX;
            } else {
              this.handleAutoSizing(inputRect, !!this.dropdownEl?.inputAutoSize);
            }
          } else {
            const newX = (inputRect.right || 0) - popupOffsetWidth;

            if (newX > 0) {
              popupRect.x = newX;
            } else {
              this.handleAutoSizing(inputRect, !!this.dropdownEl?.inputAutoSize);
            }
          }
        }

        return popupRect;
      };

      this.popup.setPosition(x, y, false, true);
    }
  }

  /**
   * Configures the IdsListBox element associated with this IdsDropdownList.
   */
  configureListBox() {
    this.listBox = this.querySelector<IdsListBox>('ids-list-box');

    // If no list box element is present as a direct descendant,
    // assume usage inside IdsDropdown and search for slotted ListBox
    if (!this.listBox) {
      if (this.children[0]?.tagName === 'SLOT') {
        this.listBox = (this.children[0] as HTMLSlotElement).assignedElements()?.[0] as IdsListBox;
      }
    }

    if (this.autoSize) this.listBox?.setAttribute(attributes.AUTO_SIZE, this.autoSize.toString());

    if (this.listBox && this.dropdownEl?.inputAutoSize === false) {
      this.listBox.setAttribute(attributes.INPUT_AUTO_SIZE, 'false');
    }

    // IdsListBox has styles that are dependent on field height/compact settings,
    // but doesn't implement IdsFieldHeightMixin, so these are passed here.
    if (this.listBox) {
      if (this.compact && !this.listBox?.hasAttribute(attributes.COMPACT)) {
        this.listBox.setAttribute(attributes.COMPACT, 'true');
      }

      if (this.listBox.maxHeight && this.popup) {
        this.popup.maxHeight = this.listBox.maxHeight;
      }
    }

    this.syncRowIndex();
    this.configureDescription();
  }

  /**
   * Synchronizes row index values for all list box options to maintain proper sort order
   * @returns {void}
   */
  syncRowIndex() {
    if (this.listBox?.options?.length) {
      this.listBox.options.forEach((option: IdsListBoxOption, index) => {
        if (option.rowIndex >= 0) return;
        option.rowIndex = index; // row-index used to keep track of the sort-order of options
      });
    }
  }

  /**
   * Add internal aria attributes while open
   * @private
   * @returns {void}
   */
  private setAriaOnMenuOpen(): void {
    this.setAttribute(htmlAttributes.ARIA_EXPANDED, 'true');

    // Add aria for the open state
    const selected = this.selectedOption || this.querySelector('ids-list-box-option:not([group-label])');
    this.listBox?.setAttribute(attributes.TABINDEX, '0');

    if (selected && this.value) {
      this.selectOption(selected);
    }
  }

  /**
   * Adds internal aria attributes while closed
   * @returns {void}
   */
  setAriaOnMenuClose() {
    this.removeAttribute(htmlAttributes.ARIA_EXPANDED);
    this.listBox?.removeAttribute(attributes.TABINDEX);

    const selected = this.selected;

    if (selected) {
      this.deselectOption(selected);
    }
  }

  /**
   * Returns the currently available options
   * @returns {Array<any>} the array of options
   */
  get options() {
    return this.querySelectorAll<IdsListBoxOption>('ids-list-box-option');
  }

  /**
   * Returns the selected Listbox option based on the Dropdown's value.
   * @returns {IdsListBoxOption| null} the selected option
   */
  get selectedOption(): IdsListBoxOption | null {
    const selector = this.generateSelectorForListBoxOption(this.value || '');
    return this.querySelector(selector);
  }

  /**
   * Returns the currently-selected Listbox option
   * (may be different from the Dropdown's value because of user input)
   * @readonly
   * @returns {IdsListBoxOption|null} Reference to a selected Listbox option if one is present
   */
  get selected(): IdsListBoxOption | null {
    const child = this.children[0];
    if (child?.tagName === 'SLOT') {
      return this.listBox?.querySelector('ids-list-box-option.is-selected') || null;
    }
    return this.querySelector('ids-list-box-option.is-selected') || null;
  }

  /**
   * Set typeahead attribute
   * @param {string | boolean | null} value typeahead value
   */
  set typeahead(value: string | boolean | null) {
    const val = stringToBool(value);

    if (val) {
      this.setAttribute(attributes.TYPEAHEAD, String(val));
    } else {
      this.removeAttribute(attributes.TYPEAHEAD);
    }

    this.container?.classList.toggle('typeahead', val);
  }

  /**
   * Get the typeahead attribute
   * @returns {boolean} typeahead attribute value converted to boolean
   */
  get typeahead(): boolean {
    return stringToBool(this.getAttribute(attributes.TYPEAHEAD));
  }

  set positionStyle(val: 'fixed' | 'absolute' | null) {
    this.setAttribute(attributes.POSITION_STYLE, val === 'fixed' ? 'fixed' : 'absolute');
    this.container?.style.setProperty('position', val === 'fixed' ? 'fixed' : 'absolute');
    this.popup?.setAttribute(attributes.POSITION_STYLE, this.positionStyle);
  }

  get positionStyle(): 'fixed' | 'absolute' {
    return this.getAttribute(attributes.POSITION_STYLE) === 'fixed' ? 'fixed' : 'absolute';
  }

  /**
   * Set the value of the dropdown using the value/id attribute if present
   * @param {string} value The value/id to use
   */
  set value(value: string | null | undefined) {
    let selector = this.generateSelectorForListBoxOption(value || '');
    const isCombobox = this.classList.contains('is-combobox');

    if (value === ' ' || value === null) selector = `ids-list-box-option:not([value])`;
    else if (isCombobox) selector = `ids-list-box-option`;
    const elem = this.listBox?.querySelector<IdsListBoxOption>(selector);
    if (!elem) return;

    this.clearSelected();
    this.selectOption(elem);
    this.setAttribute(attributes.VALUE, String(value));
  }

  get value(): string | null | undefined {
    return this.getAttribute(attributes.VALUE);
  }

  /**
   * Set the auto-size of input
   * @param {string} value  The value of auto-size
   */
  set autoSize(value: string) {
    this.setAttribute(attributes.AUTO_SIZE, value);
  }

  get autoSize(): boolean {
    return stringToBool(this.getAttribute(attributes.AUTO_SIZE));
  }

  /**
   * Remove the aria and state from the currently selected element
   */
  clearSelected() {
    const option = this.listBox?.querySelector<IdsListBoxOption>('ids-list-box-option[aria-selected]');
    this.deselectOption(option);
  }

  /**
   * Gets a reference to an IdsDropdownList option based on a provided value
   * @param {string} val referenced value
   * @returns {IdsListBoxOption | null} element representing the provided value
   */
  getOption(val: string) {
    const selector = this.generateSelectorForListBoxOption(val);
    const matches = this.listBox?.querySelectorAll<IdsListBoxOption>(selector);
    if (!matches || matches.length <= 1) return matches?.[0];

    // Disambiguate duplicate values using stored selection data
    const dropdown = this.dropdownEl as IdsDropdown;
    const storedId = dropdown?.selectedOptionData?.id;
    const storedLabel = dropdown?.selectedOptionData?.label;
    if (storedId || storedLabel) {
      const match = Array.from(matches).find(
        (opt) => opt.getAttribute('id') === storedId || opt.textContent?.trim() === storedLabel
      );
      if (match) return match;
    }

    return matches[0];
  }

  /**
   * Set the aria and state on the element
   * @param {IdsListBoxOption | string} option the option to select
   * @private
   */
  selectOption(option: IdsListBoxOption | string) {
    if (!this?.popup?.visible) return;

    // Clear any previous selections first
    this.clearSelected();

    let targetOption: IdsListBoxOption | undefined | null;
    if (typeof option === 'string') {
      targetOption = this.getOption(option);
    } else {
      targetOption = option;
    }
    if (!targetOption) return;

    // Prevent selecting description options and no-results options
    if (targetOption.hasAttribute(attributes.DESCRIPTION) || targetOption.hasAttribute(attributes.NO_RESULTS)) return;

    targetOption.setAttribute(htmlAttributes.ARIA_SELECTED, 'true');
    targetOption.classList.add('is-selected');
    targetOption.setAttribute(attributes.TABINDEX, '0');
    if (this.dropdownEl) this.dropdownEl.state.selectedIndex = targetOption.rowIndex;

    const optionIcon = targetOption.querySelector('ids-icon');
    if (optionIcon) {
      const color = optionIcon.getAttribute(attributes.COLOR);
      if (color) {
        optionIcon.setAttribute(attributes.DATA_OG_COLOR, color);
        optionIcon.removeAttribute(attributes.COLOR);
      }
    }

    targetOption.focus();

    if (targetOption.id) {
      this.listBox?.setAttribute(htmlAttributes.ARIA_ACTIVE_DESCENDANT, targetOption.id);
    }
  }

  /**
   * Removes selected attributes from an option
   * @param {HTMLElement} option element to remove attributes
   */
  deselectOption(option: HTMLElement | undefined | null) {
    option?.removeAttribute(htmlAttributes.ARIA_SELECTED);
    option?.classList.remove('is-selected');
    option?.setAttribute(attributes.TABINDEX, '-1');

    const optionIcon = option?.querySelector('ids-icon');
    if (optionIcon) {
      const originalColor = optionIcon.getAttribute(attributes.DATA_OG_COLOR);
      if (originalColor) {
        optionIcon.setAttribute(attributes.COLOR, originalColor);
      }
    }
  }

  onAllowBlankChange(val: boolean) {
    if (val) this.insertBlank();
    else this.removeBlank();
  }

  onClearableTextChange() {
    if (this.allowBlank) {
      this.removeBlank();
      this.insertBlank();
    }
  }

  onDescriptionChange(val: string | string[] | null) {
    if (val) this.insertDescription();
    else this.removeDescription();
  }

  onColorVariantRefresh(val: string | null) {
    if (this.popup && val?.length) {
      this.popup.type = val;
    }
  }

  /**
   * Insert blank option into list box
   */
  private insertBlank(): void {
    if (this.getOption('blank')) {
      return;
    }

    const list = this.listBox;
    const blankOption = `<ids-list-box-option value="blank" aria-label="Blank">${this.clearableText ?? '&nbsp;'}</ids-list-box-option>`;
    this.removeBlank();
    list?.insertAdjacentHTML('afterbegin', blankOption);
  }

  /**
   * Remove blank options from list box
   */
  private removeBlank(): void {
    this.getOption('blank')?.remove();
  }

  /**
   * Refreshes the state of the "blank" option in the Dropdown list
   * @returns {void}
   */
  configureBlank() {
    if (this.allowBlank) this.insertBlank();
    else this.removeBlank();
  }

  /**
   * Insert description option(s) into list box
   */
  private insertDescription(): void {
    const list = this.listBox;
    const descriptions = this.description;
    this.removeDescription();

    if (descriptions && Array.isArray(descriptions)) {
      descriptions.forEach((desc, index) => {
        const descriptionOption = `<ids-list-box-option value="description-${index}" disabled="true" description aria-label="Description">${desc}</ids-list-box-option>`;
        list?.insertAdjacentHTML('beforeend', descriptionOption);
      });
    }
  }

  /**
   * Remove description options from list box
   */
  private removeDescription(): void {
    const options = this.listBox?.querySelectorAll(`ids-list-box-option[${attributes.DESCRIPTION}]`);
    options?.forEach((option) => option.remove());
  }

  /**
   * Refreshes the state of the "description" option in the Dropdown list
   * @returns {void}
   */
  configureDescription() {
    if (this.description) this.insertDescription();
    else this.removeDescription();
  }

  onFieldHeightChange(val: string) {
    const attr = val === attributes.COMPACT ? { name: attributes.COMPACT, val: '' } : { name: attributes.FIELD_HEIGHT, val };
    if (val) {
      this.listBox?.setAttribute(attr.name, attr.val);
    } else {
      this.listBox?.removeAttribute(attributes.COMPACT);
      this.listBox?.removeAttribute(attributes.FIELD_HEIGHT);
    }
  }

  onSizeChange(value: string) {
    if (value) this.listBox?.setAttribute(attributes.SIZE, value);
    else this.listBox?.removeAttribute(attributes.SIZE);
  }

  /**
   * Uses a currently-highlighted list item to "navigate" a specified number
   * of steps to another list item, highlighting it.
   * @param {number} [amt] positive/negative/0 value
   * @returns {Element | null} the item that will be highlighted
   */
  navigate(amt = 0) {
    const selected = this.selected;
    const next = selected?.nextElementSibling;
    const prev = selected?.previousElementSibling;
    let currentItem: Element | null = null;

    if (amt > 0 && next) {
      const isGroupLabel = next.hasAttribute(attributes.GROUP_LABEL);
      const isDescription = next.hasAttribute(attributes.DESCRIPTION);

      if ((isGroupLabel || isDescription) && !next.nextElementSibling) return currentItem;
      currentItem = (isGroupLabel || isDescription) ? next.nextElementSibling : next;
    }

    if (amt < 0 && prev) {
      const isGroupLabel = prev.hasAttribute(attributes.GROUP_LABEL);
      const isDescription = prev.hasAttribute(attributes.DESCRIPTION);

      if ((isGroupLabel || isDescription) && !prev.previousElementSibling) return currentItem;
      currentItem = (isGroupLabel || isDescription) ? prev.previousElementSibling : prev;
    }

    if (currentItem) {
      this.deselectOption(selected);
      this.selectOption(currentItem as IdsListBoxOption);
    }

    return currentItem;
  }

  /**
   * Performs a "selection" on the list and notifies the Dropdown component
   */
  select() {
    const value = this.selected?.getAttribute(attributes.VALUE) || '';
    this.value = value;
    this.triggerSelectedEvent();
  }

  canTooltipShow(tooltipEl: IdsTooltip, tooltipContent: string) {
    if (tooltipContent && this.lastHovered) {
      return checkOverflow(this.lastHovered) || false;
    }
    return false;
  }

  /**
   * Store original tooltip data from options
   */
  private storeOriginalTooltips() {
    if (!this.dropdownEl?.tooltip) return;

    const options = this.dropdownEl.querySelectorAll('ids-list-box-option[tooltip]');
    this.originalTooltips = new Map();

    options.forEach((option) => {
      const value = option.getAttribute('value');
      const tooltip = option.getAttribute('tooltip');
      if (value && tooltip) {
        this.originalTooltips.set(value, tooltip);
      }
    });
  }

  /**
   * Restore tooltip attributes to options after DOM changes
   */
  private restoreTooltips() {
    if (!this.originalTooltips || !this.listBox) return;

    const options = this.listBox.querySelectorAll('ids-list-box-option');
    options.forEach((option) => {
      const value = option.getAttribute('value');
      if (value && this.originalTooltips.has(value)) {
        const tooltip = this.originalTooltips.get(value);
        if (tooltip) option.setAttribute('tooltip', tooltip);
      }
    });
  }

  /**
   * Move focus to next/previous focusable element
   * @param {boolean} forward - true for next, false for previous
   */
  private moveFocus(forward: boolean): void {
    this.dropdownEl?.moveFocus(forward);
  }
}
